/*
 * What a line on a pharmacy order bills at.
 *
 * This lived inside the Sale Entry line grid, which is where the figures are
 * typed. The nurse's Order History has to print the same figures back for an
 * order that is already billed, and two copies of a pricing rule is one copy
 * too many - the rule moved here, and the grid imports it.
 */

/** Pack rate down to a single unit, where the medicine comes in strips. */
export const perUnitRate = (item, packRate) => {
  const conversion = item.pharmacy_medicine_strip_conversion;
  return conversion ? (packRate || 0) / conversion : packRate || 0;
};

/**
 * Breaking a strip open to sell a few tablets is priced differently from
 * selling whole strips, so a part-strip quantity bills at the medicine's loose
 * price rather than the pack rate divided down.
 *
 * Applies only below one full strip: at a full strip or more the customer is
 * effectively buying packs, and the normal per-unit rate applies.
 *
 * Falls back to the per-unit rate when no loose price is configured, so a
 * medicine without one bills exactly as it did before.
 */
export const isLooseSale = (item, qty) => {
  const conversion = Number(item?.pharmacy_medicine_strip_conversion || 0);
  return conversion > 0 && qty > 0 && qty < conversion;
};

export const looseUnitRate = (item) => {
  const loose = Number(item?.pharmacy_medicine_loose_price ?? NaN);
  return Number.isFinite(loose) && loose > 0 ? loose : null;
};

/** The rate a line should bill at, given how many units are being sold. */
export const effectiveRate = (item, qty, perUnit, overridden = false) => {
  if (overridden || !isLooseSale(item, qty)) return perUnit;
  return looseUnitRate(item) ?? perUnit;
};

export const computeDiscountAmount = (item, row, discountMode, qty) => {
  const raw = row.discount || 0;
  if (discountMode === "rupees") return raw;
  return (raw / 100) * qty * (row.rate || 0);
};

/**
 * What a saved line came to: quantity dispensed, at its rate, less its own
 * discount, plus the GST due on what is left.
 *
 * The order's items carry no amount of their own, so this is read off the
 * fields that decide one - the rate the pharmacist set, or failing that the
 * price of the batch the medicine actually went out on.
 *
 * Returns null where there is nothing to bill: a line that was never supplied
 * is not a line worth nothing, it is a line with no figure at all, and the two
 * read very differently in a column of money.
 */
export const billedLineAmount = (item, batchSellingPrice) => {
  const qty = Number(item?.dispense_qty) || 0;
  if (qty <= 0) return null;

  const packRate =
    batchSellingPrice ??
    item.pharmacy_medicine_rate_a ??
    item.pharmacy_medicine_mrp ??
    0;

  const overridden = item.override_rate != null;
  const rate = effectiveRate(
    item,
    qty,
    overridden ? Number(item.override_rate) : perUnitRate(item, Number(packRate)),
    overridden
  );

  /* Stored as rupees off the line - the entry screen writes it back in the
     unit it defaults to. */
  const taxable = Math.max(qty * rate - (Number(item.discount) || 0), 0);
  const gst = taxable * ((Number(item.pharmacy_medicine_gst_percentage) || 0) / 100);
  return taxable + gst;
};

/**
 * What the pharmacy actually went out and charged for, per order line.
 *
 * A dispense names the batch each line left on, and the batch is what sets the
 * price, so the two are read together.
 */
export const dispensedPrices = (order) => {
  const prices = new Map();
  (order?.dispenses || []).forEach((dispense) => {
    (dispense.items || []).forEach((line) => {
      if (line.order_item != null && line.selling_price != null) {
        prices.set(line.order_item, Number(line.selling_price));
      }
    });
  });
  return prices;
};

/**
 * What an order comes to, dispensed or not.
 *
 * `billedLineAmount` prices what actually went out, which is nil on an order
 * the pharmacy has not touched yet - and a pending order still has a value:
 * what it is asking for. So a line is priced on what was dispensed where
 * something was, and on what was requested where nothing has been.
 */
export const orderLineValue = (item, batchSellingPrice) => {
  const dispensed = Number(item?.dispense_qty) || 0;
  if (dispensed > 0) return billedLineAmount(item, batchSellingPrice);

  const qty = Number(item?.requested_qty) || 0;
  if (qty <= 0) return null;

  return billedLineAmount({ ...item, dispense_qty: qty }, batchSellingPrice);
};

export const orderValue = (order) => {
  const prices = dispensedPrices(order);
  return (order?.items || []).reduce(
    (sum, item) => sum + (orderLineValue(item, prices.get(item.id)) ?? 0),
    0
  );
};

/** The order's own total, where the server has not sent one. */
export const billedOrderTotal = (order) => {
  const prices = dispensedPrices(order);
  return (order?.items || []).reduce(
    (sum, item) => sum + (billedLineAmount(item, prices.get(item.id)) ?? 0),
    0
  );
};
